// PromptComposer (DESIGN §3.4.2): Eingabe der Bar-AI-Plattform, baut auf dem Composer auf.
// Pill 56 (Radius 24, ab zwei Zeilen 20), wächst bis 8 Zeilen; links „+“ (Anhang aus dem Company
// Storage), rechts Senden 36 bzw. Stopp während des Streamens. Varianten `centered` (AI1, mit
// SuggestionChips darunter) und `docked` (AI2, unten fest, Breite = Lesespalte).
// Tastatur: Enter sendet (nicht bei `pointer: coarse`, nie während IME), Shift+Enter Zeile, Esc stoppt,
// ↑ im leeren Feld bearbeitet die letzte Frage, ⌘⇧C kopiert die letzte Antwort.
import type { ReactNode } from 'react';
import { Composer, type ComposerProps } from './Composer';
import { cn } from '../basis/kern';

export interface PromptComposerProps extends Omit<
  ComposerProps,
  | 'size'
  | 'surface'
  | 'aria-label'
  | 'mentions'
  | 'readOnlyReason'
  | 'editing'
  | 'onCancelEdit'
  | 'context'
  | 'emoji'
  | 'onEmoji'
> {
  /** Standard „Message Bar AI“. */
  'aria-label'?: string;
  /** `centered`: mittig unter dem Gruß (AI1) · `docked`: unten in der Gesprächsansicht (AI2). */
  variant?: 'centered' | 'docked';
  /** Unter der Pill, nur `centered`: Einstiegsfragen (SuggestionChips). */
  suggestions?: ReactNode;
  /** Kleiner Hinweis unter der Pill, z. B. „Answers use only data you can see.“ */
  footnote?: ReactNode;
}

export const PROMPT_PLACEHOLDER = 'Ask about people, goals, reviews, apps or company files';

export function PromptComposer({
  variant = 'docked',
  suggestions,
  footnote,
  placeholder = PROMPT_PLACEHOLDER,
  className,
  ...rest
}: PromptComposerProps) {
  const { 'aria-label': ariaLabel = 'Message Bar AI', ...composer } = rest;
  return (
    <div
      data-variant={variant}
      className={cn(
        'mx-auto flex w-full max-w-ai-read flex-col',
        variant === 'centered' ? 'gap-6' : 'gap-2 bg-page pt-2 pb-3',
        className,
      )}
    >
      <Composer
        {...composer}
        aria-label={ariaLabel}
        placeholder={placeholder}
        size="lg"
        surface="page"
      />
      {footnote ? <p className="text-center text-small text-fg-tertiary">{footnote}</p> : null}
      {variant === 'centered' && suggestions ? suggestions : null}
    </div>
  );
}
